import React from "react";
import { ChangeLog } from "../../models/ChangeLog";
import Typography from "../../components/Core/Typography";
import IconButton from "../../components/Core/IconButton";
import { ReactComponent as CloseIcon } from '../../assets/icons/x.svg';
import { ReactComponent as UserIcon } from '../../assets/icons/user.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import './changeLogPanel.scss';
import { StationService } from "../../api";
import { DataLoadingContext } from "../../components/DataLoading";
import { ReactComponent as BackIcon } from '../../assets/icons/arrow-left.svg';
import Tooltip from "../../components/Core/Tooltip";

// Internal interfaces for parsing the 'changes' string
interface ChangeEntry {
    oldVal: string;
    newVal: string;
}

interface ParsedChanges {
    [propertyName: string]: ChangeEntry;
}

// Extended ChangeLog interface for internal use after parsing
interface ProcessedChangeLog extends ChangeLog {
    ParsedChanges?: ParsedChanges;
}

interface Props {
    close: () => void;
    id: string;
}

const ChangeLogPanel: React.FC<Props> = ({ close, id }) => {
    const [selectedLog, setSelectedLog] = React.useState<ProcessedChangeLog | null>(null);
    const [changeLogs, setChangeLogs] = React.useState<ChangeLog[]>([]);

    const { toggleLoading } = React.useContext(DataLoadingContext);
    React.useEffect(() => {
        toggleLoading(true);
        StationService.getChangeLogsForStation(id).then((res) => {
            setChangeLogs(res);
        }).finally(() => {
            toggleLoading(false);
        })
    }, [id])

    const processedChangeLogs: ProcessedChangeLog[] = React.useMemo(() => {
        if (!changeLogs || changeLogs.length === 0) return [];
        return changeLogs.map(log => {
            try {
                return {
                    ...log,
                    ParsedChanges: JSON.parse(log.changes) as ParsedChanges
                };
            } catch (e) {
                return { ...log, ParsedChanges: undefined };
            }
        });
    }, [changeLogs]);

    const formatValue = (val: string | null | undefined, propertyName?: string): string | JSX.Element => {
        let items: string[] = [];
        const multiValueProperties = ['Formats', 'Aliases'];

        if (val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
            return <Typography variant="body">N/A</Typography>;
        }

        if (typeof val === 'string' && multiValueProperties.includes(propertyName || '')) {
            items = val.split(',').map(item => item.trim());
        } else {
            return <Typography variant="body">{String(val)}</Typography>;
        }

        return (
            <div className="multi-input-value-container">
                {items.map((item, index) => (
                    <span key={index} className="multiple-value-text-input-item">
                        <Typography variant="body" color="primary">
                            {item}
                        </Typography>
                    </span>
                ))}
            </div>
        );
    };


    const renderChangeDetails = (propertyName: string, change: ChangeEntry) => {
        if (propertyName === 'Contact' || propertyName === 'DJ') {
            const oldValIsEmpty = !change.oldVal || change.oldVal.trim() === '';
            const newValIsEmpty = !change.newVal || change.newVal.trim() === '';

            if (oldValIsEmpty && !newValIsEmpty) {
                return (
                    <div key={propertyName} className="change-item-detail-entry">
                        <Typography variant="title" color="Primary" weight={400}>
                            {propertyName}
                        </Typography>
                        <div className="current-value">
                            <Typography variant="body" color="success">
                                Added {change.newVal} to {propertyName === 'Contact' ? "contacts" : "dj list"}.
                            </Typography>
                        </div>
                    </div>
                );
            }

            if (newValIsEmpty && !oldValIsEmpty) {
                return (
                    <div key={propertyName} className="change-item-detail-entry">
                        <Typography variant="title" color="Primary" weight={400}>
                            {propertyName}
                        </Typography>
                        <div className="previous-value">
                             <Typography variant="body" color="error">
                                Removed {change.oldVal} from {propertyName === 'Contact' ? "contacts" : "dj list"}.
                            </Typography>
                        </div>
                    </div>
                );
            }
        }

        const oldValDisplay = formatValue(change.oldVal, propertyName);
        const newValDisplay = formatValue(change.newVal, propertyName);

        const hasOldVal = change.oldVal && change.oldVal.trim() !== '';
        const hasNewVal = change.newVal && change.newVal.trim() !== '';

        if (!hasOldVal && !hasNewVal) {
            return null;
        }

        return (
            <div key={propertyName} className="change-item-detail-entry">
                <Typography variant="title" color="Primary" weight={400}>
                    {propertyName}
                </Typography>
                <div className="value-pair">
                    {hasOldVal && (
                        <div className="previous-value">
                            <Typography variant="body" weight={500}>
                                Previous Value:
                            </Typography>
                            {oldValDisplay}
                        </div>
                    )}
                    {hasNewVal && (
                        <div className="current-value">
                            <Typography variant="body" weight={500}>
                                Current Value:
                            </Typography>
                            {newValDisplay}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const handleViewDetails = (log: ProcessedChangeLog) => {
        setSelectedLog(log);
    };


    return (
        <div className="change-log-panel">
            <div className='panel-header'>
                <Typography variant="heading" color="Primary" weight={600}>
                    CHANGE LOG
                </Typography>
                <div className='panel-actions'>
                    {selectedLog &&
                        <Tooltip text="Back to List"><IconButton Icon={BackIcon} onClick={() => { setSelectedLog(null) }} aria-label="Back to list" /></Tooltip>
                    }
                    <Tooltip text="Close Panel"><IconButton Icon={CloseIcon} onClick={() => { setSelectedLog(null); close() }} aria-label="Close panel" /></Tooltip>
                </div>
            </div>

            {selectedLog ? (
                <div>
                    <div className="change-log-detail-view">
                        <div className="change-log-detail-meta">
                            <div className="inline-item">
                                <UserIcon />
                                <Typography variant="body" color="Secondary" weight={500}>
                                    Updated by: {selectedLog.updatedByName}
                                </Typography>
                            </div>
                            <div className="inline-item">
                                <CalendarIcon />
                                <Typography variant="body" color="Secondary" weight={500}>
                                    On: {new Date(selectedLog.dateCreated).toLocaleString()}
                                </Typography>
                            </div>
                        </div>

                        <div className="changes-detail">
                            {selectedLog.ParsedChanges && Object.entries(selectedLog.ParsedChanges).length > 0 && (
                                Object.entries(selectedLog.ParsedChanges).map(([propertyName, change]) => (
                                    renderChangeDetails(propertyName, change)
                                ))
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                processedChangeLogs.length > 0 ? (
                    <div className="change-log-summary-list-boxes">
                        {processedChangeLogs
                            .sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime())
                            .map((log) => {
                                const propertyNames = log.ParsedChanges ? Object.keys(log.ParsedChanges).join(', ') : '';
                                const displayProperties = propertyNames.length > 35 ? `${propertyNames.substring(0, 35)}...` : propertyNames;
                                return (

                                    <div key={log.id} className="change-log-summary-item" onClick={() => handleViewDetails(log)}>
                                        <div className="change-log-item-detail">
                                            <div className="inline-item">
                                                <UserIcon />
                                                <Typography variant="body" weight={500}>
                                                    {log.updatedByName}
                                                </Typography>
                                            </div>
                                            <div className="inline-item">
                                                <CalendarIcon />
                                                <Typography variant="body" color="textSecondary">
                                                    {new Date(log.dateCreated).toLocaleString()}
                                                </Typography>
                                            </div>
                                            <div className="inline-item">
                                                <EditIcon />
                                                <Typography variant="body" color="textSecondary">
                                                    {displayProperties}
                                                </Typography>
                                            </div>
                                        </div>
                                        <div className="change-log-item-actions">
                                            <button onClick={(e) => { e.stopPropagation(); handleViewDetails(log); }}>
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                )
                            })}
                    </div>
                ) : (
                    <Typography variant="body" >
                        No change logs available.
                    </Typography>
                )
            )}
        </div>
    );
};

export default ChangeLogPanel;
